import { remark } from 'remark';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import { v4 as uuidv4 } from 'uuid';

import { DecorateHtmlRules } from '../../renderers/StaticMarkdownRenderer';

export const constructHTMLFromMarkdown = async (markdownContent: string) => {
  const processedContent = await remark()
    .use(remarkGfm)
    .use(remarkHtml, { sanitize: false, closeEmptyElements: true })
    .process(markdownContent);
  return processedContent.toString();
};

export const decorateMarkdownHtmlRules: DecorateHtmlRules = {
  p: (_node, children: React.ReactNode) => <p className="mb-2"> {children}</p>,
  // The post title is already the page h1, so a markdown "#" is demoted to h2 rather than
  // emitting a second h1. The explicit text-* classes pin the sizes, so nothing changes visually.
  h1: (_node, children: React.ReactNode) => <h2 className="tracking-tight mt-6 mb-10 text-5xl"> {children} </h2>,
  h2: (_node, children: React.ReactNode) => <h2 className="tracking-tight mt-4 mb-8 text-4xl"> {children} </h2>,
  h3: (_node, children: React.ReactNode) => <h3 className="tracking-tight mb-5 text-3xl"> {children} </h3>,
  // h4 and h5 both rendered as <h3>, collapsing three levels into one and flattening the outline.
  h4: (_node, children: React.ReactNode) => <h4 className="tracking-tight mb-3 text-2xl"> {children} </h4>,
  h5: (_node, children: React.ReactNode) => <h5 className="tracking-tight mb-3 text-xl"> {children} </h5>,
  th: (_node, children: React.ReactNode) => (
    <th className="pb-2" scope="col">
      {children}
    </th>
  ),
  td: (_node, children: React.ReactNode) => <td className="text-sm py-1.5"> {children} </td>,
  ol: (_node, children: React.ReactNode) => <ol className="pl-4 pb-2"> {children} </ol>,
  ul: (_node, children: React.ReactNode) => <ul className="pl-4 pb-2"> {children} </ul>,
  li: (_node, children: React.ReactNode) => (
    <li key={`li-item-${uuidv4()}`} className="list-disc">
      {children}
    </li>
  ),
  a: (node, children: React.ReactNode) => {
    const href = node.attribs.href;
    if (href.includes('http') && href.endsWith('.pdf')) {
      // Treat PDF links as iframes
      return <iframe src={href} width="100%" height="800px"></iframe>;
    }
    // Otherwise, treat them as regular links
    return (
      <a className="text-blue-500 underline" target="_blank" href={node.attribs.href} rel="noreferrer noopener">
        {' '}
        {children}{' '}
      </a>
    );
  },
};
